/** O que está sendo digitado no cursor: `@nome`, `#canal` ou `:emoji`. */
export type Gatilho = {
  tipo: '@' | '#' | ':';
  termo: string;
  /** Onde começa o gatilho (o `@`) e onde termina (o cursor). */
  inicio: number;
  fim: number;
};

/**
 * Acha o gatilho antes do cursor (só no começo ou depois de espaço, como o
 * Discord). `:` precisa de 2 letras; fora de bloco de código.
 */
export function acharGatilho(texto: string, cursor: number): Gatilho | null {
  const antes = texto.slice(0, cursor);
  const crases = (antes.match(/`/g) ?? []).length;
  if (crases % 2 === 1) return null;
  const achado = /(^|\s)([@#:])([^\s@#:]{0,32})$/u.exec(antes);
  if (!achado) return null;
  const tipo = achado[2] as Gatilho['tipo'];
  const termo = achado[3];
  if (tipo === ':' && termo.length < 2) return null;
  return {
    tipo,
    termo,
    inicio: cursor - termo.length - 1,
    fim: cursor,
  };
}

/** Troca o gatilho pelo escolhido (com um espaço depois). */
export function substituir(
  texto: string,
  gatilho: Gatilho,
  inserido: string,
): { texto: string; cursor: number } {
  const depois = texto.slice(gatilho.fim);
  const espaco = depois.startsWith(' ') ? '' : ' ';
  const novo = `${texto.slice(0, gatilho.inicio)}${inserido}${espaco}${depois}`;
  return { texto: novo, cursor: gatilho.inicio + inserido.length + 1 };
}

function normalizar(texto: string) {
  return texto
    .normalize('NFD')
    .replace(/\p{Diacritic}/gu, '')
    .toLowerCase();
}

/**
 * Ordena por: começa com o termo, depois contém; desempate pelo `extra`
 * (online antes) e pelo nome.
 */
export function filtrarPorTermo<T>(
  itens: T[],
  termo: string,
  textos: (item: T) => string[],
  limite = 10,
  extra: (item: T) => number = () => 0,
): T[] {
  const busca = normalizar(termo);
  const pontuados: Array<{ item: T; nota: number; nome: string }> = [];
  for (const item of itens) {
    const lista = textos(item).map(normalizar);
    let nota = -1;
    if (!busca) nota = 1;
    else if (lista.some((texto) => texto.startsWith(busca))) nota = 2;
    else if (lista.some((texto) => texto.includes(busca))) nota = 1;
    if (nota < 0) continue;
    pontuados.push({
      item,
      nota: nota * 10 + extra(item),
      nome: lista[0] ?? '',
    });
  }
  return pontuados
    .sort((a, b) => b.nota - a.nota || a.nome.localeCompare(b.nome, 'pt-BR'))
    .slice(0, limite)
    .map((pontuado) => pontuado.item);
}
